import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Check,
  Sparkles,
  ShoppingCart,
  Award,
  Trophy,
  ShieldCheck,
  KeyRound,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  CheckCircle2,
  Loader2,
  Inbox,
} from 'lucide-react';
import {
  useGetNotificationsQuery,
  useGetNotificationUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} from '../../api/apiSlice';

// NotificationsBell
// ─────────────────
// Bell-иконка в шапке + dropdown с последними уведомлениями.
//
// Принципы:
//   • Polling badge-счётчика каждые 60s (только когда вкладка видна) — дёшево.
//   • Список грузим лениво только при открытии dropdown'а.
//   • Click outside закрывает.
//   • Icon + tone по семейству типа (notif.{family}.* → tone).
//   • i18n: titleKey/bodyKey + interpolation с metadata.

// Иконка + цветной кружок для каждого «семейства» уведомлений.
const TYPE_VIEW = {
  welcome:     { Icon: Sparkles,      tone: 'bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300' },
  order:       { Icon: ShoppingCart,  tone: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300' },
  certificate: { Icon: Award,         tone: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300' },
  course:      { Icon: Trophy,        tone: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300' },
  instructor:  { Icon: TrendingUp,    tone: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300' },
  payout:      { Icon: DollarSign,    tone: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-300' },
  refund:      { Icon: AlertTriangle, tone: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300' },
  security:    { Icon: ShieldCheck,   tone: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300' },
  password:    { Icon: KeyRound,      tone: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300' },
  default:     { Icon: Bell,          tone: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
};
const viewFor = (type) => TYPE_VIEW[String(type).split('.')[0]] || TYPE_VIEW.default;

function timeAgo(iso, t) {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return t('notif.timeJustNow');
  const min = Math.floor(sec / 60);
  if (min < 60) return t('notif.timeMinAgo', { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t('notif.timeHoursAgo', { count: hr });
  const day = Math.floor(hr / 24);
  return t('notif.timeDaysAgo', { count: day });
}

const NotificationsBell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  // Координаты dropdown'а — считаем при открытии. Portal вытаскивает его
  // в document.body, поэтому стекинг-контекст layout-header'а / sticky-секций
  // в /learn не накроет дроп.
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

  const recalcPosition = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    // top = ниже кнопки + 8px. right = расстояние от правого края вьюпорта
    // до правого края кнопки (для right-alignment).
    setDropdownPos({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  };

  // Пересчёт при ресайзе и скролле — иначе drop "оторвётся" от кнопки.
  useLayoutEffect(() => {
    if (!open) return undefined;
    recalcPosition();
    window.addEventListener('resize', recalcPosition);
    window.addEventListener('scroll', recalcPosition, true);
    return () => {
      window.removeEventListener('resize', recalcPosition);
      window.removeEventListener('scroll', recalcPosition, true);
    };
  }, [open]);

  // Polling badge — 60s. Хук сам paused-ит когда tab hidden благодаря
  // RTK Query (skipPollingIfUnfocused опции нет; вместо неё мы polling
  // строим через interval prop, а сама RTK при background-tab переходит
  // в throttled mode браузером).
  const { data: countData } = useGetNotificationUnreadCountQuery(undefined, {
    pollingInterval: 60_000,
  });
  const unreadCount = countData?.count || 0;

  // Список — грузим только при первом открытии (skip = !open && !ever).
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const { data: listData, isFetching, refetch: refetchList } = useGetNotificationsQuery(
    { limit: 15 },
    { skip: !hasOpenedOnce }
  );
  const items = listData?.items || [];

  // Когда polling badge'а замечает изменение количества — рефетчим список,
  // чтобы dropdown не оказался пустым (только что прилетевшая уведа должна
  // быть видна сразу при клике, без необходимости рефреша страницы).
  // Сравниваем с предыдущим значением через ref, чтобы не дёргать рефетч
  // на каждый ре-рендер.
  const prevCountRef = useRef(unreadCount);
  useEffect(() => {
    if (unreadCount !== prevCountRef.current) {
      prevCountRef.current = unreadCount;
      if (hasOpenedOnce) refetchList();
    }
  }, [unreadCount, hasOpenedOnce, refetchList]);

  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead, { isLoading: isMarkingAll }] = useMarkAllNotificationsReadMutation();

  // Click outside → закрыть.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Escape → закрыть.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleToggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    // Первое открытие триггерит query через skip→false. Последующие открытия —
    // forced refetch (защита от случая «badge новый, list устарел»).
    if (hasOpenedOnce) {
      refetchList();
    } else {
      setHasOpenedOnce(true);
    }
  };

  const handleItemClick = async (n) => {
    setOpen(false);
    if (!n.readAt) {
      // Не дожидаемся ответа — UX-приоритет: моментальная навигация.
      markRead(n.id);
    }
    if (n.link) navigate(n.link);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="relative inline-flex items-center justify-center h-10 w-10 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
        aria-label={t('notif.header')}
        title={t('notif.header')}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-pink-500 text-white text-[10px] font-bold tabular-nums shadow-md ring-2 ring-white dark:ring-slate-900">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && createPortal(
        // Portal в document.body — выходим из стекинг-контекста layout-header'а,
        // sticky-секций CourseViewer'а и любых других потенциальных клипперов.
        // Mobile: full-width minus 8px по краям. Desktop: ширина 420px, right
        // выровнен по правому краю кнопки.
        <div
          className="fixed left-2 right-2 sm:left-auto sm:w-[420px] max-h-[70vh] rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 shadow-2xl overflow-hidden flex flex-col"
          style={{
            top: dropdownPos.top,
            right: window.innerWidth >= 640 ? dropdownPos.right : undefined,
            zIndex: 9999,
          }}
          // Сами-же остановка propagation, чтобы клик внутри drop не считался
          // "вне" (см. mousedown-handler выше — он смотрит на rootRef).
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                {t('notif.header')}
              </h3>
              {unreadCount > 0 && (
                <span className="inline-flex items-center rounded-full bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] font-bold px-2 py-0.5">
                  {t('notif.unread', { count: unreadCount })}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead()}
                disabled={isMarkingAll}
                className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
              >
                {isMarkingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {t('notif.markAllRead')}
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {isFetching && items.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {t('notif.empty')}
                </p>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  {t('notif.emptyHint')}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((n) => <NotificationItem key={n.id} entry={n} onClick={handleItemClick} t={t} />)}
              </ul>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const NotificationItem = ({ entry, onClick, t }) => {
  const { Icon, tone } = viewFor(entry.type);
  const title = t(entry.titleKey, entry.metadata || {});
  const body = entry.bodyKey ? t(entry.bodyKey, entry.metadata || {}) : null;
  const isUnread = !entry.readAt;

  return (
    <li>
      <button
        type="button"
        onClick={() => onClick(entry)}
        className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition ${
          isUnread ? 'bg-indigo-50/40 dark:bg-indigo-950/20' : ''
        }`}
      >
        <span className={`shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-xl ${tone}`}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm truncate ${isUnread ? 'font-bold text-slate-900 dark:text-white' : 'font-medium text-slate-700 dark:text-slate-200'}`}>
              {title}
            </p>
            {isUnread && <span className="shrink-0 h-2 w-2 rounded-full bg-indigo-500" />}
          </div>
          {body && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
              {body}
            </p>
          )}
          <p className="text-[11px] text-slate-400 mt-1 tabular-nums">
            {timeAgo(entry.createdAt, t)}
          </p>
        </div>
      </button>
    </li>
  );
};

export default NotificationsBell;
